import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Typography } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import { createElement, FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import CreateModificationRequest from '../../../../entity/CreateModificationRequest';
import UploadingFile from '../../../../entity/UploadingFile';
import UploadingFilesProgress from '../../../../entity/UploadingFilesProgress';
import { DeliverableForCraetionDetailFragment } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { SPACING_REGULAR } from '../../../constant/spacing';
import CreatingModificationRequestDialog from './CreatingModificationRequestDialog';

interface Props {
  createModificationRequest: CreateModificationRequest;
  deliverable: DeliverableForCraetionDetailFragment;
  files: Array<FileReference>;
  uploadingAssetFiles: Array<UploadingFile>;
  uploadingFilesProgress: UploadingFilesProgress;
  bucketId: string | null;
  onRequestUploadFiles: (files: UploadingFile[]) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRemoveAllAssetFile: () => void;
  onPushInputtingItem: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  className?: string;
}

const CreatingModificationRequestButton: FC<Props> = ({
  createModificationRequest,
  deliverable,
  uploadingAssetFiles,
  uploadingFilesProgress,
  bucketId,
  files,
  onRequestUploadFiles,
  onRemoveAssetFile,
  onRemoveAllAssetFile,
  onPushInputtingItem,
  className,
}) => {
  const [isDialogOpen, setDialogOpen] = useState(false);

  return (
    <Root className={className}>
      <AddComment
        onClick={() => {
          setDialogOpen(true);
        }}
      >
        <AddIcon />
        <AddDescription variant="subtitle1" component="p" color="inherit">
          <FormattedMessage id="modification_request.creating_card.add_item" />
        </AddDescription>
      </AddComment>

      <CreatingModificationRequestDialog
        createModificationRequest={createModificationRequest}
        deliverable={deliverable}
        files={files}
        uploadingAssetFiles={uploadingAssetFiles}
        uploadingFilesProgress={uploadingFilesProgress}
        bucketId={bucketId}
        isOpen={isDialogOpen}
        onRequestUploadFiles={onRequestUploadFiles}
        onRemoveAssetFile={onRemoveAssetFile}
        onRequestClose={(_: any) => {
          setDialogOpen(false);
          onRemoveAllAssetFile();
        }}
        onSubmit={async (
          modificationRequestId: string,
          bucketId: string | null,
          inputDuration: string,
          inputComment: string,
          files: Array<FileReference>,
        ) => {
          await onPushInputtingItem(modificationRequestId, bucketId, inputDuration, inputComment, files).then(() => {
            setDialogOpen(false);
          });
        }}
      />
    </Root>
  );
};

const Root = styled('div')({
  display: 'inline-block',
  lineHeight: 1,
  width: '100%',
  height: 100,
  marginBottom: SPACING_REGULAR,
  border: `2px dashed ${theme.palette.grey['200']}`,
  borderRadius: '3px',
  color: theme.palette.grey['500'],
  cursor: 'pointer',
});

const AddComment = styled('div')({
  display: 'grid',
  width: '100%',
  height: '100%',
  padding: '12px',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: "'icon' 'description'",
  alignItems: 'center',
  justifyItems: 'center',
});

const AddIcon = styled(Add)({
  gridArea: 'icon',
  width: 48,
  height: 48,
});

const AddDescription = (styled(Typography)({
  gridArea: 'description',
  height: 24,
}) as unknown) as typeof Typography;

export default CreatingModificationRequestButton;
